import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { onSnapshot, collection, query, orderBy, where, limit as firestoreLimit } from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { EventType, Booking } from "@/types";
import { Timestamp } from "firebase/firestore";
import { useLocation } from "react-router-dom";

// ─── Helpers ────────────────────────────────────────────────────────────

function toDate(val: any): Date {
    if (val instanceof Timestamp) return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val === "string" || typeof val === "number") return new Date(val);
    return new Date();
}

function eventFromDoc(docSnap: any): EventType {
    const d = docSnap.data();
    return {
        ...d,
        id: docSnap.id,
        createdAt: toDate(d.createdAt),
        updatedAt: toDate(d.updatedAt),
    } as EventType;
}

function bookingFromDoc(docSnap: any): Booking {
    const d = docSnap.data();
    return {
        ...d,
        id: docSnap.id,
        startTime: toDate(d.startTime),
        endTime: toDate(d.endTime),
        createdAt: toDate(d.createdAt),
    } as Booking;
}

// ─── Context ────────────────────────────────────────────────────────────

const BOOKING_LOOKBACK_DAYS = 370;
const BOOKING_CONTEXT_LIMIT = 2500;

function bookingWindowStart(): Date {
    const date = new Date();
    date.setDate(date.getDate() - BOOKING_LOOKBACK_DAYS);
    date.setHours(0, 0, 0, 0);
    return date;
}

function todayStart(): Date {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
}

interface DataContextType {
    eventTypes: EventType[];
    bookings: Booking[];
    sites: any[];
    loading: boolean;
}

interface EventTypesContextType {
    eventTypes: EventType[];
    loading: boolean;
}

interface BookingsContextType {
    bookings: Booking[];
    loading: boolean;
}

interface SitesContextType {
    sites: any[];
    loading: boolean;
}

const EventTypesContext = createContext<EventTypesContextType>({
    eventTypes: [],
    loading: false,
});

const BookingsContext = createContext<BookingsContextType>({
    bookings: [],
    loading: false,
});

const SitesContext = createContext<SitesContextType>({
    sites: [],
    loading: false,
});

export function useData() {
    const events = useEventTypesData();
    const bookings = useBookingsData();
    const sites = useSitesData();

    return useMemo<DataContextType>(() => ({
        eventTypes: events.eventTypes,
        bookings: bookings.bookings,
        sites: sites.sites,
        loading: events.loading || bookings.loading || sites.loading,
    }), [events, bookings, sites]);
}

export function useEventTypesData() {
    return useContext(EventTypesContext);
}

export function useBookingsData() {
    return useContext(BookingsContext);
}

export function useSitesData() {
    return useContext(SitesContext);
}

function getRouteRequirements(pathname: string) {
    // Paramètres : le visuel des prochaines dates a besoin des opérations et des places restantes.
    const bookingScope = pathname.startsWith("/dashboard/bookings") || pathname.startsWith("/dashboard/settings")
        ? "upcoming"
        : pathname === "/"
        || pathname === "/electrodepot"
        || pathname.startsWith("/reservation/")
        || pathname.startsWith("/dashboard/statistics")
            ? "history"
            : null;
    const needsBookings = bookingScope !== null;

    const needsEventTypes = needsBookings
        || pathname.startsWith("/dashboard/events")
        || pathname.startsWith("/dashboard/campaigns")
        || pathname.startsWith("/dashboard/analytics")
        || pathname.startsWith("/dashboard/billing")
        || pathname.startsWith("/dashboard/transactions")
        || pathname.startsWith("/dashboard/operations")
        || pathname.startsWith("/dashboard/upcoming-operations");

    const needsSites = pathname === "/"
        || pathname === "/electrodepot"
        || pathname.startsWith("/reservation/")
        || pathname.startsWith("/dashboard");

    return { bookingScope, needsBookings, needsEventTypes, needsSites };
}

export function DataProvider({ children }: { children: ReactNode }) {
    const { pathname } = useLocation();
    const { bookingScope, needsBookings, needsEventTypes, needsSites } = getRouteRequirements(pathname);
    const [eventTypes, setEventTypes] = useState<EventType[]>([]);
    const [bookings, setBookings] = useState<Booking[]>([]);
    const [sites, setSites] = useState<any[]>([]);

    const [eventsLoaded, setEventsLoaded] = useState(false);
    const [bookingsLoadedScope, setBookingsLoadedScope] = useState<"history" | "upcoming" | null>(null);
    const [sitesLoaded, setSitesLoaded] = useState(false);

    useEffect(() => {
        if (!needsEventTypes) {
            setEventTypes([]);
            setEventsLoaded(false);
            return;
        }

        setEventsLoaded(false);
        return onSnapshot(
            collection(db, "eventTypes"),
            (snap) => {
                setEventTypes(snap.docs.map(eventFromDoc));
                setEventsLoaded(true);
            },
            (err) => {
                console.error("Error loading event types:", err);
                setEventsLoaded(true);
            }
        );
    }, [needsEventTypes]);

    useEffect(() => {
        if (!needsBookings) {
            setBookings([]);
            setBookingsLoadedScope(null);
            return;
        }

        const bookingsQuery = bookingScope === "upcoming"
            ? query(
                collection(db, "bookings"),
                where("startTime", ">=", Timestamp.fromDate(todayStart())),
                orderBy("startTime", "asc"),
            )
            : query(
                collection(db, "bookings"),
                where("startTime", ">=", Timestamp.fromDate(bookingWindowStart())),
                orderBy("startTime", "desc"),
                firestoreLimit(BOOKING_CONTEXT_LIMIT),
            );

        return onSnapshot(
            bookingsQuery,
            (snap) => {
                setBookings(snap.docs.map(bookingFromDoc));
                setBookingsLoadedScope(bookingScope);
            },
            (err) => {
                console.error("Error loading bookings:", err);
                setBookingsLoadedScope(bookingScope);
            }
        );
    }, [bookingScope, needsBookings]);

    useEffect(() => {
        if (!needsSites) {
            setSites([]);
            setSitesLoaded(false);
            return;
        }

        setSitesLoaded(false);
        return onSnapshot(
            query(collection(db, "repair_sites"), orderBy("name", "asc")),
            (snap) => {
                setSites(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                setSitesLoaded(true);
            },
            (err) => {
                console.error("Error loading sites:", err);
                setSitesLoaded(true);
            }
        );
    }, [needsSites]);

    const eventsValue = useMemo(() => ({
        eventTypes,
        loading: needsEventTypes && !eventsLoaded,
    }), [eventTypes, eventsLoaded, needsEventTypes]);
    const bookingsValue = useMemo(() => ({
        bookings,
        loading: needsBookings && bookingsLoadedScope !== bookingScope,
    }), [bookingScope, bookings, bookingsLoadedScope, needsBookings]);
    const sitesValue = useMemo(() => ({
        sites,
        loading: needsSites && !sitesLoaded,
    }), [sites, sitesLoaded, needsSites]);

    return (
        <EventTypesContext.Provider value={eventsValue}>
            <BookingsContext.Provider value={bookingsValue}>
                <SitesContext.Provider value={sitesValue}>
                    {children}
                </SitesContext.Provider>
            </BookingsContext.Provider>
        </EventTypesContext.Provider>
    );
}
